import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import { toast } from 'sonner'

export default function SeedHealthDashboard() {
  const [health, setHealth] = useState(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  useEffect(() => {
    fetchHealth()
  }, [])

  async function fetchHealth() {
    try {
      setLoading(true)
      
      const { data: { session } } = await supabase.auth.getSession()
      
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/seed-teams-health`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Failed to fetch health data')
      }

      setHealth(result)
    } catch (error) {
      console.error('Error fetching health data:', error)
      toast.error('Failed to load health dashboard')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  async function handleRefresh() {
    setRefreshing(true)
    await fetchHealth()
  }

  if (loading) {
    return (
      <Card>
        <div className="p-8 text-center text-gray-600">Loading health dashboard...</div>
      </Card>
    )
  }

  if (!health) {
    return (
      <Card>
        <div className="p-8 text-center text-gray-600">Failed to load health data</div>
      </Card>
    )
  }

  const successRate = health.total_teams > 0 
    ? Math.round((health.by_status.success / health.total_teams) * 100)
    : 0

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <div className="p-6">
            <div className="text-sm font-medium text-gray-600 mb-1">Total Teams</div>
            <div className="text-3xl font-bold text-gray-900">{health.total_teams}</div>
            <div className="text-xs text-gray-500 mt-1">
              {health.sync_enabled} sync enabled
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-6">
            <div className="text-sm font-medium text-gray-600 mb-1">Success Rate</div>
            <div className="text-3xl font-bold text-green-600">{successRate}%</div>
            <div className="text-xs text-gray-500 mt-1">
              {health.by_status.success} successful, {health.by_status.failed} failed
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-6">
            <div className="text-sm font-medium text-gray-600 mb-1">Last 24 Hours</div>
            <div className="text-3xl font-bold text-blue-600">{health.last_24h.synced}</div>
            <div className="text-xs text-gray-500 mt-1">
              {health.last_24h.new_events} new events, {health.last_24h.failed} failed
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-6">
            <div className="text-sm font-medium text-gray-600 mb-1">Active Postings</div>
            <div className="text-3xl font-bold text-purple-600">{health.auto_postings_active}</div>
            <div className="text-xs text-gray-500 mt-1">
              {health.pending_postings} pending approval
            </div>
          </div>
        </Card>
      </div>

      {/* Status Breakdown */}
      <Card>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Sync Status Breakdown</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              {refreshing ? 'Refreshing...' : '🔄 Refresh'}
            </Button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{health.by_status.success}</div>
              <div className="text-sm text-gray-600 mt-1">Success</div>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600">{health.by_status.failed}</div>
              <div className="text-sm text-gray-600 mt-1">Failed</div>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">{health.by_status.pending}</div>
              <div className="text-sm text-gray-600 mt-1">Pending</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-600">{health.by_status.never_synced}</div>
              <div className="text-sm text-gray-600 mt-1">Never Synced</div>
            </div>
          </div>
        </div>
      </Card>

      {/* Failed Teams */}
      {health.failed_teams && health.failed_teams.length > 0 && (
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Failed Syncs ({health.failed_teams.length})
            </h3>
            <div className="space-y-3">
              {health.failed_teams.map((team) => (
                <div
                  key={team.id}
                  className="p-4 bg-red-50 border border-red-200 rounded-lg"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{team.team_name}</div>
                      <div className="text-sm text-red-600 mt-1">{team.error}</div>
                      {team.last_attempted && (
                        <div className="text-xs text-gray-500 mt-1">
                          Last attempted: {new Date(team.last_attempted).toLocaleString()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Recent Activity Summary */}
      <Card>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity (Last 24 Hours)</h3>
          <div className="space-y-2 text-sm text-gray-600">
            <div className="flex justify-between">
              <span>Teams synced successfully:</span>
              <span className="font-medium text-gray-900">{health.last_24h.synced}</span>
            </div>
            <div className="flex justify-between">
              <span>Sync failures:</span>
              <span className="font-medium text-red-600">{health.last_24h.failed}</span>
            </div>
            <div className="flex justify-between">
              <span>New calendar events:</span>
              <span className="font-medium text-blue-600">{health.last_24h.new_events}</span>
            </div>
            <div className="flex justify-between">
              <span>Auto-postings created:</span>
              <span className="font-medium text-purple-600">{health.pending_postings}</span>
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}

